import { Box, Chip } from "@mui/material";
import React, { useEffect } from "react";
import AddProductVariantValues from "./AddProductVariantValues";
import { ProductOption } from "../../../../../models/Products/ProductOption";
import { ProductVariant } from "../../../../../models/Products/ProductVariant";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";

type AddProductVariantsProps = {
  productId: number;
  options: ProductOption[];
  variants: ProductVariant[];
  isViewOnly: boolean;
  onSave: (variants: ProductVariant[]) => void;
  onClear: () => void;
  canChangeCostPrice?: boolean;
};

const AddProductVariants = ({
  productId,
  options,
  variants,
  isViewOnly,
  onClear,
  onSave,
  canChangeCostPrice,
}: AddProductVariantsProps) => {
  const [selectedOptions, setSelectedOptions] = React.useState<
    ProductVariant[]
  >([]);

  const allOptions: ProductOption[] = [{ id: -1, name: "Select" }];
  allOptions.push(...options);

  useEffect(() => {
    setSelectedOptions(variants);
  }, [variants]);

  const handleDoneClick = (values: ProductVariant[]) => {
    selectedOptions.push(...values);
    onSave(variants);
  };

  const handleRemove = (value: ProductVariant) => {
    let list = selectedOptions.filter(
      (item) =>
        !(item.options_id === value.options_id && item.value === value.value)
    );

    onSave(list);
  };

  const getChipTitle = (
    variant: ProductVariant,
    needValue: boolean = true
  ): string => {
    let title = "";

    if (variant.options_name && variant.options_name.length !== 0)
      title = variant.options_name;

    if (variant.title && variant.title.length !== 0)
      title = title === "" ? variant.title : title + " - " + variant.title;

    if (needValue && variant.value && variant.value.length !== 0)
      title = title === "" ? variant.value : title + " - " + variant.value;

    if (variant.sku && variant.sku.length !== 0)
      title = title + " - " + variant.sku;

    return title;
  };

  return (
    <Box sx={{ maxWidth: "100%" }}>
      {!isViewOnly ? (
        <AddProductVariantValues
          productId={productId}
          options={allOptions}
          onDoneClick={handleDoneClick}
        />
      ) : undefined}
      <Box sx={{ marginTop: 2, display: "flex", flexWrap: "wrap" }}>
        {selectedOptions.map((item) => (
          <Box>
            {isViewOnly ? (
              <Chip sx={{ margin: 1 }} label={getChipTitle(item)} />
            ) : (
              <Chip
                sx={{ margin: 1 }}
                label={getChipTitle(item)}
                onDelete={() => handleRemove(item)}
              />
            )}
          </Box>
        ))}
      </Box>
      {!isViewOnly ? (
        <Box display="flex" flexDirection="column" gap={1}>
          {variants
            ?.filter(
              (variant) =>
                (canChangeCostPrice &&
                  variant.cost_price != null &&
                  !isNaN(variant.cost_price)) ||
                (variant.selling_price != null && !isNaN(variant.selling_price))
            )
            .map((variant) => (
              <Box
                key={variant.id}
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    backgroundColor: "#f0f0f0",
                    borderRadius: 1,
                    padding: "4px 8px",
                  }}
                >
                  {getChipTitle(variant, false) + ":"}
                </CustomTypography>

                {canChangeCostPrice &&
                  variant.cost_price != null &&
                  !isNaN(variant.cost_price) && (
                    <CustomTypography variant="body2" fontWeight={500}>
                      CP: ₹{variant.cost_price + " | "}
                    </CustomTypography>
                  )}

                {variant.selling_price != null &&
                  !isNaN(variant.selling_price) && (
                    <CustomTypography variant="body2" fontWeight={500}>
                      SP: ₹{+variant.selling_price}
                    </CustomTypography>
                  )}
              </Box>
            ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default AddProductVariants;
